// THE LIVE CATALOGUE SEARCH (STORE-03, D-11; plan 08A-16). A storefront-only
// controller: it is registered by entrypoints/storefront.js and never by
// controllers/index.js, so nothing of the instructor bundle reaches the
// storefront graph.
//
// The listing works without it: the form GETs the listing page and the cursor
// link is a plain link. With it, each change reloads the results frame from
// the cacheable `/:locale/search` (debounced while typing) and replaces the
// page's address with the listing URL for the same query, so the address is
// always the one to share. The frame's loading and error states live outside
// the frame, in the served page, hidden until needed.
import { Controller } from "@hotwired/stimulus"

const DEBOUNCE_MS = 300

export default class extends Controller {
  static targets = ["form", "query", "sort", "frame", "loading", "error"]
  static values = { endpoint: String }

  connect() {
    this.chosen = this.sortTarget.dataset.storefrontSearchChosen === "true"
    this.onStart = () => this.showLoading()
    this.onLoad = () => this.showResults()
    this.onFail = (event) => this.showError(event)
    this.frameTarget.addEventListener("turbo:before-fetch-request", this.onStart)
    this.frameTarget.addEventListener("turbo:frame-load", this.onLoad)
    this.frameTarget.addEventListener("turbo:fetch-request-error", this.onFail)
    this.frameTarget.addEventListener("turbo:frame-missing", this.onFail)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.frameTarget.removeEventListener("turbo:before-fetch-request", this.onStart)
    this.frameTarget.removeEventListener("turbo:frame-load", this.onLoad)
    this.frameTarget.removeEventListener("turbo:fetch-request-error", this.onFail)
    this.frameTarget.removeEventListener("turbo:frame-missing", this.onFail)
  }

  queue() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.apply(), DEBOUNCE_MS)
  }

  submit(event) {
    event.preventDefault()
    this.apply()
  }

  // The visitor picked an order: from now on it is sent even as the query changes.
  choose() {
    this.chosen = true
    this.apply()
  }

  apply() {
    clearTimeout(this.timer)
    this.load(this.formParams())
  }

  // «عرض المزيد»: the link's own listing parameters, loaded into the frame.
  page(event) {
    event.preventDefault()
    this.load(new URL(event.currentTarget.href).searchParams)
  }

  // The failed load again, or the form's when nothing was loaded yet.
  retry() {
    if (this.frameTarget.src) this.frameTarget.reload()
    else this.apply()
  }

  // The form's listing parameters. Relevance needs a query; an order the
  // visitor never picked is left to the server, which answers relevance with a
  // query and the academy's order without one.
  formParams() {
    const params = new URLSearchParams()
    const q = this.queryTarget.value.trim()
    const relevance = this.sortTarget.querySelector("option[value='relevance']")
    if (relevance) relevance.disabled = q === ""
    if (q !== "") params.set("q", q)
    if (this.formTarget.elements.namedItem("free")?.checked) params.set("free", "1")
    if (!this.chosen || this.sortTarget.selectedOptions[0]?.disabled) {
      this.sortTarget.value = q === "" ? "academy" : "relevance"
    }
    if (this.chosen) params.set("sort", this.sortTarget.value)
    return params
  }

  load(params) {
    const listing = new URL(this.formTarget.action, window.location.href)
    listing.search = params.toString()
    window.history.replaceState(window.history.state, "", listing.pathname + listing.search)

    const frame = new URL(this.endpointValue, window.location.href)
    params.forEach((value, key) => frame.searchParams.set(key, value))
    this.frameTarget.src = frame.pathname + frame.search
  }

  showLoading() {
    this.errorTarget.hidden = true
    this.loadingTarget.hidden = false
    this.frameTarget.hidden = true
  }

  showResults() {
    this.loadingTarget.hidden = true
    this.frameTarget.hidden = false
  }

  // A network failure, or an answer that is not the frame (an error page):
  // the previous results stay, with a retry line above them.
  showError(event) {
    if (event.type === "turbo:frame-missing") event.preventDefault()
    this.loadingTarget.hidden = true
    this.frameTarget.hidden = false
    this.errorTarget.hidden = false
  }
}
